// 多线程版引擎(wasm32-wasip1-threads 编的 web-mt.wasm)的宿主:共享内存、引擎要的七个 WASI 函数、开线程。
// 引擎线程(模拟 Worker)和帮手线程(每个一个 Worker)用同一块共享内存、同一个编好的模块;帮手只跑 wasi_thread_start,
// 在 eco/src/par.rs 的线程池里等活,不碰城市。开几个线程结果都逐字一样(见 engine/eco/PARALLEL.md)。
// 为什么用 wasip1-threads:稳定版 Rust 自带编好的、带原子操作的标准库,std::thread 底下调 wasi.thread-spawn,
// 不用 nightly 重编标准库、不用 wasm-bindgen;代价是要宿主补这几个函数,这里补齐。
// 浏览器里要 SharedArrayBuffer:页面必须带 COOP/COEP 两个响应头(scripts/serve-www.mjs),crossOriginIsolated 为真才能用。

export interface MtExports {
  memory: WebAssembly.Memory;
  par_threads(n: number): number;
  wasi_thread_start(tid: number, arg: number): void;
  init(seed: number, pop: number): void;
  step(n: number): number;
  out_len(): number;
  [name: string]: unknown;
}

/** 模块要的共享内存有多大(导入段里写的最少、最多几页)。不写死:引擎一改,最少的页数会变。 */
export function memoryLimits(bytes: Uint8Array): { initial: number; maximum: number } {
  let p = 8;
  const u = () => { let r = 0, s = 0, x = 0; do { x = bytes[p++]; r += (x & 127) * 2 ** s; s += 7; } while (x & 128); return r; };
  const skipName = () => { const n = u(); p += n; };
  while (p < bytes.length) {
    const id = bytes[p++], len = u(), end = p + len;
    if (id === 2) {
      for (let n = u(); n > 0; n--) {
        skipName(); skipName();
        const kind = bytes[p++];
        if (kind === 0) u();
        else if (kind === 1) { p++; const f = bytes[p++]; u(); if (f & 1) u(); }
        else if (kind === 3) p += 2;
        else if (kind === 2) { const f = bytes[p++]; const initial = u(); const maximum = f & 1 ? u() : 65536; return { initial, maximum }; }
      }
    }
    p = end;
  }
  throw new Error('模块没有导入共享内存:不是多线程版');
}

/** 引擎要的导入。spawn 开一个帮手线程、返回线程号(没 Worker 了返回负数,引擎就少开一个,活照样做完)。 */
export function threadImports(memory: WebAssembly.Memory, spawn: (arg: number) => number, env: string[] = [], log: (fd: number, text: string) => void = (fd, t) => (fd === 2 ? console.error : console.log)(t)) {
  const enc = new TextEncoder();
  const dec = new TextDecoder();
  const view = () => new DataView(memory.buffer);
  const envBytes = env.map((s) => enc.encode(s + '\0'));
  // WASI的指针也是i32:JS收到高于2GB的地址为负数;只还原地址位,长度与真实越界仍交给DataView/TypedArray检查。
  return {
    env: { memory },
    wasi: { 'thread-spawn': (arg: number) => spawn(arg) },
    wasi_snapshot_preview1: {
      // 随机数只给 HashMap 打散用:给固定的,开几次都一样(引擎自己的随机数在 rng.rs,不经过这里)
      random_get: (ptr: number, len: number) => { new Uint8Array(memory.buffer, ptr >>> 0, len).fill(7); return 0; },
      sched_yield: () => 0,
      clock_time_get: (_id: number, _precision: bigint, out: number) => { view().setBigUint64(out >>> 0, BigInt(Math.round(performance.now() * 1e6)), true); return 0; },
      fd_write: (fd: number, iovs: number, n: number, written: number) => {
        const v = view(), vectors = iovs >>> 0;
        let text = '', total = 0;
        for (let i = 0; i < n; i++) {
          const ptr = v.getUint32(vectors + i * 8, true), len = v.getUint32(vectors + i * 8 + 4, true);
          // 共享内存上的视图 TextDecoder 不收,先拷出来
          text += dec.decode(new Uint8Array(memory.buffer, ptr >>> 0, len).slice());
          total += len;
        }
        v.setUint32(written >>> 0, total, true);
        if (text.trim()) log(fd, text.trimEnd());
        return 0;
      },
      environ_sizes_get: (count: number, size: number) => {
        const v = view();
        v.setUint32(count >>> 0, envBytes.length, true);
        v.setUint32(size >>> 0, envBytes.reduce((s, b) => s + b.length, 0), true);
        return 0;
      },
      environ_get: (ptrs: number, buf: number) => {
        const v = view();
        let at = buf >>> 0;
        const pointers = ptrs >>> 0;
        envBytes.forEach((b, i) => { v.setUint32(pointers + i * 4, at, true); new Uint8Array(memory.buffer, at, b.length).set(b); at += b.length; });
        return 0;
      },
      proc_exit: (code: number) => { throw new Error(`引擎退出 ${code}`); },
    },
  };
}

/** 从共享内存读引擎给的 JSON 文本(先拷出来:TextDecoder 不收共享内存上的视图)。 */
export function readOut(e: MtExports, ptr: number): string {
  return new TextDecoder().decode(new Uint8Array(e.memory.buffer, ptr >>> 0, e.out_len()).slice());
}

/** 是不是手机(含平板):多线程版默认不在手机上开(发热降频、耗电,有的申请不到 4GB 上限的共享内存)。
 * 先看 userAgentData.mobile;没有的看 UA;iPad 冒充 Mac 的看触点。页面线程算好,用网址参数 mobile=1 告诉引擎线程。 */
export function mobileDevice(nav: { userAgent?: string; maxTouchPoints?: number; userAgentData?: { mobile?: boolean } } | undefined): boolean {
  if (!nav) return false;
  if (nav.userAgentData?.mobile === true) return true;
  const ua = nav.userAgent ?? '';
  return /Mobi|Android|iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1);
}

/** 一个帮手线程(浏览器、Node 各自的 Worker 包一层):load 实例化好了才 resolve;start 派线程号和入口参数进 wasi_thread_start。 */
export interface ThreadHelper { load(module: WebAssembly.Module, memory: WebAssembly.Memory): Promise<void>; start(tid: number, arg: number): void }

/** 线程局部变量的地址低于这个,就是引擎线程没拿到线程局部存储(落在主栈底,见下)。 */
export const TLS_FLOOR = 65536;

/** 建引擎线程的实例、开帮手、定线程数。游戏页、测速页、Node 的检查都走这一个,先后不许各写各的:
 * 1. 引擎线程的实例要先建、先 _initialize。共享内存的模块里,启动函数 __wasm_init_memory 用一个原子标志只让第一个实例
 *    把 .tdata 拷进静态的那块线程局部存储(1MB 栈后面)、把自己的 __tls_base 指过去;后建的实例跳过,__tls_base 停在 0。
 *    原来先建帮手,引擎线程的 __tls_base 就是 0:它的 thread_local(城市本身、实验开关、各种缓存)落在地址 0 起的十几 KB,
 *    是主栈的底,没按初值初始化、还会被写坏,开 2、4 个线程第一天就走成另一条历史(2026-10-27 查的,1 个线程不开帮手所以没事)。
 *    建好以后查一遍(tls_addr),不对就报错,页面退回单线程版。
 * 2. 再开帮手、等它们都实例化好:引擎开线程是同步调 wasi.thread-spawn 的,派一个已经就绪的过去,不等 Worker 慢慢起来。
 *    帮手的实例在进 wasi_thread_start 以前 __tls_base 是 0、栈指针是主栈顶,这以前不能调进模块的任何函数。
 * 3. 最后 par_threads 定线程数(这时才开线程)。开不出来的帮手(没 Worker 了)引擎就少开一个,活照样做完。 */
export async function startThreaded(module: WebAssembly.Module, memory: WebAssembly.Memory, n: number, newHelper: () => ThreadHelper): Promise<{ e: MtExports; helpers: ThreadHelper[]; threads: number }> {
  const helpers: ThreadHelper[] = [];
  let next = 0;
  const spawn = (arg: number) => { const h = helpers[next++]; if (!h) return -6; h.start(next, arg); return next; };
  const e = (await WebAssembly.instantiate(module, threadImports(memory, spawn))).exports as unknown as MtExports;
  // wasi-libc 的初始化(主线程的线程指针、构造函数):不调的话第一次分配内存就卡死
  (e._initialize as () => void)();
  const tls = typeof e.tls_addr === 'function' ? (e.tls_addr as () => number)() >>> 0 : null;
  if (tls !== null && tls < TLS_FLOOR) throw Object.assign(new Error(`引擎线程没拿到线程局部存储(地址 ${tls}),不用多线程版`), { code: 'tls' });
  for (let k = 1; k < n; k++) {
    const h = newHelper();
    helpers.push(h);
    await h.load(module, memory);
  }
  return { e, helpers, threads: e.par_threads(n) };
}

/** 帮手线程里跑:用同一个模块、同一块内存实例化,等引擎线程派来线程号和入口参数,进 wasi_thread_start(不回来)。 */
export function helperInstance(module: WebAssembly.Module, memory: WebAssembly.Memory): MtExports {
  return new WebAssembly.Instance(module, threadImports(memory, () => -6)).exports as unknown as MtExports;
}
